import $ from 'jquery';
import {
    Bounds, Logger, namespace, Point, View,
} from '../../../../../../FrontendBundle/Resources/js/ofcs';
import CanvasDraggable from '../../canvas/draggable';

let handleSymbolsAlreadyInitialized = false;

/**
 * Represents the resize handle on a svg canvas.
 *
 * @class   Ofcs.Editor.Svg.Handle.AbstractResizeHandle
 * @extends Ofcs.View
 */
export default class AbstractResizeHandle extends View {
    /**
     * Size of a handle
     *
     * @constant {number}
     */
    HANDLE_SIZE = 12;

    /**
     * Item which will be resized
     *
     * @protected
     * @type {ItemInterface}
     */
    item = null;

    /**
     * Item which will be resized
     *
     * @protected
     * @type {Ofcs.Editor.Svg.Canvas}
     */
    canvas = null;

    /**
     * Set of handles
     *
     * @protected
     * @type {Object}
     */
    handles = {};

    /**
     * Initializes a resize handle
     *
     * @param {Ofcs.Editor.Item.ItemInterface} item Item which should be resized
     * @param {Ofcs.Editor.Svg.Canvas} canvas Canvas which receives the handle
     */
    constructor(item, canvas) {
        super();
        Logger.debug('HandleResizeAbstract initialize');

        this.handles = {};
        this.item = item;
        this.canvas = canvas;

        const layerManager = canvas.getLayerManager();
        const handlesLayer = layerManager.getLayer('ui.handles');
        this.group = handlesLayer.group();
        $(this.group).data('item', item);

        this.initializeHandleSymbols();
        this.initializePointers(item);

        this.updateElementsPositions();

        const dragAndDrop = new CanvasDraggable(this.canvas, this.group);
        dragAndDrop.on('dragStart', (...args) => this.onPointerDragStart(...args));
        dragAndDrop.on('drag', (...args) => this.onPointerDrag(...args));
        dragAndDrop.on('dragStop', (...args) => this.onPointerDragStop(...args));

        item.on('change', (...args) => this.onItemChange(...args));
        canvas.on('zoomchange', () => this.updateElementsPositions());
        canvas.on('panchange', () => this.updateElementsPositions());
    }

    /**
     * Initializes handle symbols for reuse. All symbols are placed
     * in the global `<defs>' node. This method will be canceled if
     * symbols are already initialized.
     *
     * @protected
     */
    initializeHandleSymbols() {
        const svg = this.canvas.getSvgWrapper();
        const defs = this.canvas.getDefsNode();

        if (handleSymbolsAlreadyInitialized) {
            return;
        }

        Logger.debug('abstractResizeHandle.initializeHandleSymbols()');

        const symbol = svg.symbol(defs, 'handleResizePoint', 0, 0, this.HANDLE_SIZE, this.HANDLE_SIZE);

        svg.linearGradient(
            defs,
            'handleResizeBg',
            [[0, '#C56271'], [1, '#A45278']],
            0,
            0,
            0,
            1,
        );
        svg.linearGradient(
            defs,
            'handleResizeStrokeColor',
            [[0.5, '#A45278'], [1, '#480E0E']],
            0,
            0,
            0,
            1,
        );
        svg.linearGradient(
            defs,
            'handleResizeInvalidBg',
            [[0, '#ADADAD'], [1, '#6E6E6E']],
            0,
            0,
            0,
            1,
        );
        svg.linearGradient(
            defs,
            'handleResizeStrokeInvalidColor',
            [[0.5, '#6E6E6E'], [1, '#383838']],
            0,
            0,
            0,
            1,
        );
        svg.rect(symbol, 0, 0, this.HANDLE_SIZE, this.HANDLE_SIZE, 1, 1, {
            fill: 'black',
            stroke: 'none',
            opacity: 0.2,
        });
        svg.rect(symbol, 1, 1, this.HANDLE_SIZE - 1, this.HANDLE_SIZE - 1, 1, 1, {
            fill: 'url(#handleResizeBg)',
            stroke: 'url(#handleResizeStrokeColor)',
            strokeWidth: 0.5,
            style: 'cursor: pointer',
        });

        // tableleg background-pattern
        const lines = svg.pattern(defs, 'diagonalHatch', 0, 0, 40, 40, { patternUnits: 'userSpaceOnUse' });

        svg.path(lines, 'M 0,40 l 40,-40 M -10,10 l 20,-20 M 30,50 l 20,-20', {
            stroke: '#404B51',
            'stroke-width': 23,
            'stroke-linecap': 'square',
            'stroke-opacity': 0.1,
            'shape-rendering': 'auto',
        });

        handleSymbolsAlreadyInitialized = true;
    }

    /**
     * Initialize drag handles. To be implemented in subclasses.
     *
     * @protected
     */
    initializePointers() {
    }

    /**
     * Create the correct resize command
     *
     * @protected
     */
    initializeCommand() {
    }

    /**
     * Calculate the position for each point based on the given center, the size of the recangle and its rotation
     *
     * @protected
     *
     * @param {Point} position
     * @return {{
     *      topLeft: Point,
     *      topCenter: Point,
     *      topRight: Point,
     *      left: Point,
     *      right: Point,
     *      bottomLeft: Point,
     *      bottomCenter: Point,
     *      bottomRight: Point
     * }}
     */
    calculateHandlePointsForPosition(position) {
        const bounds = this.getNormalizedItemBounds(position);

        const points = {
            topLeft: bounds.getTopLeft(),
            topCenter: bounds.getTopCenter(),
            topRight: bounds.getTopRight(),
            centerLeft: bounds.getCenterLeft(),
            centerRight: bounds.getCenterRight(),
            bottomLeft: bounds.getBottomLeft(),
            bottomCenter: bounds.getBottomCenter(),
            bottomRight: bounds.getBottomRight(),
        };

        Object
            .values(points)
            .forEach(point => point.transform(this.getRotationMatrix(position)));

        return points;
    }

    getNormalizedItemBounds(position) {
        return this.getNormalizedItemBoundsBasedOnCenterPosition(position);
    }

    getNormalizedItemBoundsBasedOnCenterPosition(center) {
        const width = this.item.get('width');
        const height = this.item.get('height');

        const topLeft = new Point(center.x - (width / 2), center.y - (height / 2));
        const bottomRight = new Point(center.x + (width / 2), center.y + (height / 2));
        return new Bounds(topLeft, bottomRight);
    }

    getNormalizedItemBoundsBasedOnTopLeftPosition(topLeft) {
        const width = this.item.get('width');
        const height = this.item.get('height');

        const bottomRight = new Point(topLeft.x + width, topLeft.y + height);
        return new Bounds(topLeft, bottomRight);
    }

    /**
     * Updates the position of internal DOM elements
     *
     * @protected
     */
    updateElementsPositions() {
        const { options } = this.canvas;

        const zoomMatrix = new Ofcs.Matrix()
            .translate(-this.HANDLE_SIZE / 2, -this.HANDLE_SIZE / 2)
            .scale(options.zoom)
            .translate(options.panX, options.panY);

        const points = this.calculateHandlePointsForPosition(this.item.getAbsolutePosition());

        Object
            .keys(this.handles)
            .forEach(key => this.handles[key].element.attr(points[key].transform(zoomMatrix)));
    }

    /**
     * Do resizing of the rectangle item.
     *
     * @protected
     *
     * @param {Marker} marker The dragged marker
     * @param {Point} newAbsoluteMarkerPosition The new position of the moved resize handle after dragging.
     */
    resizeItem(marker, newAbsoluteMarkerPosition) {
        if (this.configureResizeCommand(marker, newAbsoluteMarkerPosition) !== false) {
            this.canvas.getCommandExecutor().execute(this.command);
        }
    }

    getNormalizedPoint(point) {
        return point.clone().transform(this.getRotationMatrix(this.item.get('position')).invert());
    }

    getTransformedPoint(point) {
        return point.clone().transform(this.getRotationMatrix(this.item.get('position')));
    }

    getRotationMatrix(center) {
        const rotation = this.item.get('rotation');
        const matrix = new Ofcs.Matrix();

        if (rotation) {
            matrix.rotate(rotation, center);
        }

        return matrix;
    }

    // eslint-disable-next-line no-unused-vars
    configureResizeCommand(marker, newAbsoluteMarkerPosition) {
    }

    /**
     * Create a handle at the specified position.
     *
     * @protected
     *
     * @param {DOMElement} parentEl
     * @return {jQuery}
     */
    createPointerElement() {
        const svg = this.canvas.getSvgWrapper();

        return $(svg.rect(this.group, 0, 0, this.HANDLE_SIZE - 1, this.HANDLE_SIZE - 1, 1, 1, {
            fill: 'url(#handleResizeBg)',
            stroke: 'url(#handleResizeStrokeColor)',
            strokeWidth: 0.5,
            style: 'cursor: pointer',
            class: 'resize-handle',
        }));
    }

    /**
     * Removes the DOM nodes from the canvas and deactivates all event listeners
     */
    remove() {
        // Remove all listeners from item
        this.item.off(null, null, this);

        // Remove elements from DOM
        $(this.group).remove();
    }

    /**
     * Callback which is called each time an attribute of #item is changed.
     *
     * @protected
     *
     * @param {ItemInterface} item The item which was modified. Equal to #item
     */
    onItemChange(item) {
        if (item.hasChangedProperty('position', 'rotation', 'width', 'height')) {
            this.updateElementsPositions();
        }
    }

    /**
     * Callback method which is called when dragging of pointer starts.
     *
     * Based on the dragged handle the correct resizing function (closure)
     * is generated and stored in the member dragFn, which is called on each drag event.
     *
     * @protected
     *
     * @param {Object} event Drag event. See CanvasDraggable#drag
     */
    onPointerDragStart(event) {
        Logger.debug('resize rect on drag start');

        this.canvas.focus();

        const draggedMarker = Object
            .values(this.handles)
            .find(handle => handle.element[0] === event.target);

        if (draggedMarker) {
            this.dragFn = point => this.resizeItem(draggedMarker, point);
        } else {
            this.dragFn = () => Logger.warn(
                'AbstractResizeHandle onPointerDragStart unknown target',
                event.target,
            );
        }

        this.initializeCommand();
    }

    /**
     * Callback method which is called each movement while dragging.
     *
     * It is calling the dragFn closure created in the dragStart callback.
     *
     * @protected
     *
     * @param {Object} event Drag event. See CanvasDraggable#drag
     */
    onPointerDrag(event) {
        Logger.debug('resize item on drag');

        if (typeof this.dragFn === 'function') {
            this.dragFn(event.point);
        }
    }

    /**
     * Callback method which is called when dragging of pointer ends
     *
     * @protected
     *
     * @param {Object} event Drag event. See CanvasDraggable#drag
     */
    onPointerDragStop() {
        Logger.debug('resize item on drag stop');

        const history = this.canvas.getHistory();

        history.addCommand(this.command);
        delete this.command;
    }
}

namespace('Ofcs.Editor.Svg.Handle');
window.Ofcs.Editor.Svg.Handle.AbstractResizeHandle = AbstractResizeHandle;
